/*
 * Book a Private Viewing (product page): a viewing of this piece, booked without leaving the page.
 * LDJ, Oct 2026. Markup and words: sections/product.liquid (data-pdp-book).
 *
 * The dialog opens on the start screen, as before 8 Oct (LDJ, 9 Oct): Book in Person, Schedule
 * FaceTime, Prefer to talk. The two buttons open our own booking, as short as LDJ's Calendly:
 * 2. a time, 3. first name, phone (the FaceTime number) and email. In person it's a visit at the
 * boutique where the piece is. The phone number is formatted and checked as it's typed.
 * Prefer to talk shows the specialist where the piece is, with Call and Text; nothing to fill in.
 * The LDJ Search app (GET /api/viewings?kind=…) reads the specialist's Google Calendar and sends
 * the open times; they show as a Sunday-to-Saturday calendar in the shopper's own time zone.
 * POST /api/viewings books it on the calendar and Google emails the shopper the invite.
 * Without a connected calendar the same steps send a request the specialist confirms, and if the
 * times can't load the shopper can still leave their details. "Talk to a Product Specialist" at
 * the foot goes back to the start screen's Talk form.
 */
(() => {
  const root = document.querySelector('[data-pdp-book]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';

  const d = root.dataset;
  const dialog = root.closest('[data-pdp-viewing]');
  const opener = document.querySelector('[data-pdp-viewing-open]');
  const q = (selector) => root.querySelector(selector);
  const form = q('[data-pdp-book-form]');
  const steps = {};
  root.querySelectorAll('[data-pdp-book-step]').forEach((step) => {
    steps[step.dataset.pdpBookStep] = step;
  });
  const back = q('[data-pdp-book-back]');
  const count = q('[data-pdp-book-count]');
  const loadingEl = q('[data-pdp-book-loading]');
  const calendar = q('[data-pdp-book-calendar]');
  const monthEl = q('[data-pdp-book-month]');
  const daysEl = q('[data-pdp-book-days]');
  const timesEl = q('[data-pdp-book-times]');
  const zoneEl = q('[data-pdp-book-zone]');
  const takenEl = q('[data-pdp-book-taken]');
  const unavailable = q('[data-pdp-book-unavailable]');
  const timeNext = steps['2'].querySelector('[data-pdp-book-next]');
  const whenEl = q('[data-pdp-book-when]');
  const withEl = q('[data-pdp-book-with]');
  const errorEl = q('[data-pdp-book-error]');
  const submit = q('[data-pdp-book-submit]');
  const calendars = q('[data-pdp-book-calendars]');
  const startEl = q('[data-pdp-book-start]');
  const phoneLabel = q('[data-pdp-book-phone-label]');

  // The steps each kind of viewing goes through.
  const FLOWS = { facetime: ['2', '3'], visit: ['2', '3'] };
  const STORE = `ldj:viewing:${d.variantId}`;
  let kind = 'facetime';
  let current = 'start';
  // Open times, per kind of viewing (a visit is longer than a FaceTime).
  const cache = { facetime: { data: null, at: 0, loading: null }, visit: { data: null, at: 0, loading: null } };
  let chosen = null;
  let dayKey = null;
  let record = null;
  const city = () => (cache.visit.data && cache.visit.data.city) || d.city || 'our';
  const isVisit = () => kind === 'visit';

  // ------------------------------------------------------------ dates, in the shopper's own zone

  let zone = '';
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_error) {
    zone = '';
  }
  const zoneName = (() => {
    for (const style of ['longGeneric', 'long']) {
      try {
        const part = new Intl.DateTimeFormat('en-US', { timeZoneName: style }).formatToParts(new Date()).find((item) => item.type === 'timeZoneName');
        if (part) return part.value;
      } catch (_error) {
        // older browsers: try the next style
      }
    }
    return 'your time zone';
  })();
  const format = (options) => new Intl.DateTimeFormat('en-US', options);
  const keyOf = (date) => format({ year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  const timeText = (date) => format({ hour: 'numeric', minute: '2-digit' }).format(date);
  const dayText = (date) => format({ weekday: 'long', month: 'long', day: 'numeric' }).format(date);
  const whenText = (date) => `${dayText(date)} at ${timeText(date)}`;

  // ------------------------------------------------------------ steps

  // The start screen: Book in Person, Schedule FaceTime, Prefer to talk.
  const showStart = ({ focus = true } = {}) => {
    current = 'start';
    root.classList.add('is-start');
    dialog.setAttribute('aria-labelledby', 'PdpBookTitle-start');
    root.scrollTop = 0;
    if (focus) startEl.querySelector('#PdpBookTitle-start')?.focus({ preventScroll: true });
  };

  const setKind = (next) => {
    if (next !== kind) {
      chosen = null;
      dayKey = null;
    }
    kind = next;
    root.dataset.kind = kind;
    phoneLabel.textContent = isVisit() ? 'Phone' : 'FaceTime Number';
    submit.textContent = isVisit() ? 'Book My Visit' : 'Book My Viewing';
  };

  const show = (name, { focus = true } = {}) => {
    current = name;
    root.classList.remove('is-start');
    root.dataset.kind = kind;
    Object.keys(steps).forEach((key) => {
      steps[key].hidden = key !== name;
    });
    const order = FLOWS[kind];
    const index = order.indexOf(name);
    // The first step's Back returns to the start screen.
    back.hidden = index < 0;
    count.hidden = index < 0;
    if (index >= 0) count.textContent = `Step ${index + 1} of ${order.length}`;
    dialog.setAttribute('aria-labelledby', `PdpBookTitle-${name}`);
    root.scrollTop = 0;
    if (name === '2') renderSlots();
    if (focus) steps[name].querySelector('.pdp-book__title')?.focus({ preventScroll: true });
  };

  back.addEventListener('click', () => {
    const order = FLOWS[kind];
    const index = order.indexOf(current);
    if (index > 0) show(order[index - 1]);
    else showStart();
  });

  // Book in Person / Schedule FaceTime: our booking, in place of the old Calendly page.
  root.querySelectorAll('[data-pdp-book-go]').forEach((link) => {
    const next = link.dataset.pdpBookGo === 'visit' ? 'visit' : 'facetime';
    const prefetch = () => loadSlots(false, next);
    link.addEventListener('pointerenter', prefetch, { once: true });
    link.addEventListener('focus', prefetch, { once: true });
    link.addEventListener('click', (event) => {
      event.preventDefault();
      setKind(next);
      show(FLOWS[next][0]);
    });
  });

  // Prefer to talk: the specialist's number, Call and Text. Nothing to fill in (LDJ, 9 Oct).
  const talkToggle = startEl.querySelector('[data-pdp-talk-toggle]');
  const talkPanel = startEl.querySelector('[data-pdp-talk-panel]');
  const showTalk = (open) => {
    if (!talkToggle || !talkPanel) return;
    talkPanel.hidden = !open;
    talkToggle.setAttribute('aria-expanded', String(open));
  };
  if (talkToggle) {
    talkToggle.hidden = false;
    talkToggle.addEventListener('click', () => showTalk(talkPanel.hidden));
  }
  // From a booking step's foot: back to the start screen with the number showing.
  q('[data-pdp-book-talk]')?.addEventListener('click', () => {
    showStart({ focus: false });
    showTalk(true);
    window.setTimeout(() => talkPanel?.querySelector('.pdp-call__number')?.focus({ preventScroll: true }), 60);
  });
  timeNext.addEventListener('click', () => {
    fillSummary();
    show('3');
  });
  q('[data-pdp-book-change]').addEventListener('click', () => show('2'));

  // ------------------------------------------------------------ open times

  const loadSlots = (fresh = false, which = kind) => {
    const entry = cache[which];
    if (!fresh && entry.data && Date.now() - entry.at < 60000) return Promise.resolve(entry.data);
    if (entry.loading && !fresh) return entry.loading;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 9000);
    const url = `${d.endpoint}?shop=${encodeURIComponent(d.shop)}&specialist=${encodeURIComponent(d.specialist)}&kind=${which}`;
    entry.loading = fetch(url, { signal: controller.signal, credentials: 'omit' })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
      .then((data) => {
        entry.data = data && Array.isArray(data.slots) ? data : null;
        entry.at = Date.now();
        return entry.data;
      })
      .catch(() => {
        entry.data = null;
        return null;
      })
      .finally(() => {
        window.clearTimeout(timer);
        entry.loading = null;
      });
    return entry.loading;
  };

  const button = (className, label) => {
    const element = document.createElement('button');
    element.type = 'button';
    element.className = className;
    if (label) element.textContent = label;
    return element;
  };

  const PERIODS = [
    { name: 'Morning', test: (hour) => hour < 12 },
    { name: 'Afternoon', test: (hour) => hour >= 12 && hour < 17 },
    { name: 'Evening', test: (hour) => hour >= 17 },
  ];

  const renderTimes = (byDay) => {
    const slots = byDay.get(dayKey) || [];
    const groups = PERIODS.map((period) => ({ ...period, slots: slots.filter((slot) => period.test(slot.start.getHours())) })).filter((group) => group.slots.length);
    timesEl.replaceChildren(
      ...groups.map((group) => {
        const wrap = document.createElement('div');
        wrap.className = 'pdp-book__period';
        const heading = document.createElement('p');
        heading.className = 'pdp-book__period-name';
        heading.textContent = group.name;
        const grid = document.createElement('div');
        grid.className = 'pdp-book__time-grid';
        group.slots.forEach((slot) => {
          const time = button('pdp-book__time', timeText(slot.start));
          time.setAttribute('aria-pressed', String(Boolean(chosen && chosen.iso === slot.iso)));
          time.setAttribute('aria-label', whenText(slot.start));
          time.addEventListener('click', () => {
            chosen = slot;
            timesEl.querySelectorAll('.pdp-book__time').forEach((other) => other.setAttribute('aria-pressed', String(other === time)));
            timeNext.disabled = false;
            takenEl.hidden = true;
          });
          grid.append(time);
        });
        wrap.append(heading, grid);
        return wrap;
      }),
    );
  };

  let renderRun = 0;
  const renderSlots = async (fresh = false) => {
    const run = ++renderRun;
    if (!cache[kind].data || fresh) {
      loadingEl.hidden = false;
      calendar.hidden = true;
      unavailable.hidden = true;
    }
    const data = await loadSlots(fresh);
    if (run !== renderRun) return;
    loadingEl.hidden = true;

    const slots = data ? data.slots.map((slot) => ({ iso: slot.start, start: new Date(slot.start), end: new Date(slot.end) })) : [];
    if (!slots.length) {
      // No times to show: they can still leave their details and the specialist arranges one.
      calendar.hidden = true;
      unavailable.hidden = false;
      unavailable.textContent = data
        ? `There are no open times in the next two weeks. Leave your details and ${d.name} will find a time with you.`
        : `We couldn’t load ${d.name}’s calendar just now. Leave your details and ${d.name} will arrange a time with you.`;
      zoneEl.textContent = isVisit() ? `A visit with ${d.name} at our ${city()} boutique.` : `A short FaceTime with ${d.name}.`;
      chosen = null;
      timeNext.disabled = false;
      return;
    }

    const minutes = data.lengthMinutes || (isVisit() ? 30 : 15);
    const what = isVisit() ? `A ${minutes}-minute visit with ${d.name} at our ${city()} boutique.` : `A ${minutes}-minute FaceTime with ${d.name}.`;
    zoneEl.textContent = `${what} Times are in ${zoneName}.${data.mode === 'request' ? ` ${d.name} will confirm your time.` : ''}`;
    unavailable.hidden = true;
    calendar.hidden = false;

    const byDay = new Map();
    slots.forEach((slot) => {
      const key = keyOf(slot.start);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(slot);
    });
    if (chosen && !slots.some((slot) => slot.iso === chosen.iso)) chosen = null;
    if (!dayKey || !byDay.has(dayKey)) dayKey = chosen ? keyOf(chosen.start) : keyOf(slots[0].start);
    timeNext.disabled = !chosen;

    // A calendar, Sunday to Saturday (LDJ, 9 Oct): from this week's Sunday to the Saturday after
    // the last open day. Days already gone show greyed, like the closed weekends.
    const days = [];
    const lastKey = keyOf(slots[slots.length - 1].start);
    const todayKey = keyOf(new Date());
    const cursor = new Date();
    cursor.setHours(12, 0, 0, 0);
    cursor.setDate(cursor.getDate() - cursor.getDay());
    let reachedLast = false;
    for (let i = 0; i < 42; i++) {
      days.push(new Date(cursor));
      if (keyOf(cursor) === lastKey) reachedLast = true;
      if (reachedLast && cursor.getDay() === 6) break;
      cursor.setDate(cursor.getDate() + 1);
    }
    const first = days[0];
    const end = days[days.length - 1];
    const month = format({ month: 'long' });
    monthEl.textContent = month.format(first) === month.format(end) ? format({ month: 'long', year: 'numeric' }).format(first) : `${month.format(first)} – ${format({ month: 'long', year: 'numeric' }).format(end)}`;

    let pastToday = false;
    daysEl.replaceChildren(
      ...days.map((day) => {
        const key = keyOf(day);
        const open = byDay.has(key);
        if (key === todayKey) pastToday = true;
        const chip = button('pdp-book__day');
        const date = document.createElement('strong');
        date.textContent = String(day.getDate());
        chip.append(date);
        // The 1st of a month says which month it is.
        if (day.getDate() === 1) {
          const name = document.createElement('span');
          name.textContent = format({ month: 'short' }).format(day);
          chip.prepend(name);
        }
        if (key === todayKey) chip.classList.add('is-today');
        if (!pastToday) chip.classList.add('is-past');
        chip.disabled = !open;
        chip.setAttribute('aria-pressed', String(key === dayKey));
        chip.setAttribute('aria-label', `${dayText(day)}${key === todayKey ? ', today' : ''}${open ? '' : ', no times'}`);
        chip.addEventListener('click', () => {
          dayKey = key;
          daysEl.querySelectorAll('.pdp-book__day').forEach((other) => other.setAttribute('aria-pressed', String(other === chip)));
          renderTimes(byDay);
        });
        return chip;
      }),
    );
    renderTimes(byDay);
  };

  // ------------------------------------------------------------ details and booking

  const fillSummary = () => {
    const minutes = (cache[kind].data && cache[kind].data.lengthMinutes) || (isVisit() ? 30 : 15);
    if (chosen) {
      whenEl.textContent = whenText(chosen.start);
      withEl.textContent = isVisit() ? `With ${d.name} at our ${city()} boutique · ${minutes} minutes` : `FaceTime with ${d.name} · ${minutes} minutes`;
    } else {
      whenEl.textContent = 'A time that suits you';
      withEl.textContent = `${d.name} will be in touch to arrange it`;
    }
  };

  // ------------------------------------------------------------ the details, checked as they're typed

  // U.S. and Canadian numbers (and the rest of +1) are formatted as (949) 555-0123; anywhere else
  // the shopper starts with + and their country code, as Calendly asks.
  const NANP_COUNTRIES = ['US', 'CA', 'PR', 'VI', 'GU', 'AS', 'MP', 'BS', 'BB', 'BM', 'JM', 'TT', 'KY', 'VG', 'AG', 'DM', 'GD', 'KN', 'LC', 'VC', 'SX', 'TC', 'AI', 'MS', 'DO'];
  const localIsNanp = !d.country || NANP_COUNTRIES.includes(d.country);
  const phoneInput = form.elements.phone;
  const emailInput = form.elements.email;
  const nameInput = form.elements.name;
  phoneInput.placeholder = localIsNanp ? '(949) 555-0123' : '+44 7700 900123';
  const fieldError = (input) => q(`[data-pdp-book-field-error="${input.name}"]`);

  const formatNanp = (digits) => {
    if (digits.length <= 3) return digits.length ? `(${digits}` : '';
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  /** { ok, value (what's sent), message } for what's in the phone field. */
  const checkPhone = (raw) => {
    const value = raw.trim();
    const digits = value.replace(/\D/g, '');
    if (!digits) return { ok: false, message: isVisit() ? `Add a phone number so ${d.name} can reach you.` : 'Add the number you use for FaceTime.' };
    if (/[^+\d\s().-]/.test(value)) return { ok: false, message: 'Use numbers only.' };
    const international = value.startsWith('+');
    if (international && !digits.startsWith('1')) {
      if (digits.length < 8 || digits.length > 15) return { ok: false, message: 'Check the number and the country code.' };
      return { ok: true, value: `+${digits}` };
    }
    if (!international && !localIsNanp) return { ok: false, message: 'Start with + and your country code, like +44.' };
    const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (local.length !== 10) return { ok: false, message: 'Enter all 10 digits, like (949) 555-0123.' };
    if (!/^[2-9]\d{2}[2-9]/.test(local)) return { ok: false, message: 'Check the area code.' };
    return { ok: true, value: `+1 ${formatNanp(local)}` };
  };
  const checkEmail = (raw) => {
    const value = raw.trim();
    if (!value) return { ok: false, message: 'Add your email for the calendar invite.' };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) return { ok: false, message: 'Check your email address.' };
    return { ok: true, value };
  };
  const checkName = (raw) => (raw.trim().length >= 2 ? { ok: true, value: raw.trim() } : { ok: false, message: 'Add your first name.' });
  const CHECKS = { name: checkName, phone: checkPhone, email: checkEmail };

  const showFieldError = (input, message) => {
    const slot = fieldError(input);
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      if (slot) {
        slot.textContent = message;
        slot.hidden = false;
      }
    } else {
      input.removeAttribute('aria-invalid');
      if (slot) slot.hidden = true;
    }
  };

  // Format a +1 number as it's typed (only while typing at the end, so editing in the middle works).
  phoneInput.addEventListener('input', (event) => {
    const value = phoneInput.value;
    if (value.trim().startsWith('+') || phoneInput.selectionStart !== value.length || event.inputType === 'deleteContentBackward') return;
    if (!localIsNanp) return;
    let digits = value.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (digits.length > 10) return;
    phoneInput.value = formatNanp(digits);
  });

  // Checked when they leave a field (not while typing), and cleared as soon as it's right.
  [nameInput, phoneInput, emailInput].forEach((input) => {
    input.addEventListener('blur', () => {
      if (!input.value.trim()) return;
      const result = CHECKS[input.name](input.value);
      showFieldError(input, result.ok ? '' : result.message);
    });
    input.addEventListener('input', () => {
      if (input.getAttribute('aria-invalid') && CHECKS[input.name](input.value).ok) showFieldError(input, '');
    });
  });

  const fail = (text, field) => {
    if (field) {
      showFieldError(field, text);
      field.focus();
      return;
    }
    errorEl.textContent = text;
    errorEl.hidden = false;
  };

  form.addEventListener('input', () => {
    if (!errorEl.hidden) errorEl.hidden = true;
  });

  const busy = (on) => {
    submit.disabled = on;
    submit.toggleAttribute('aria-busy', on);
    submit.textContent = on ? 'Booking…' : isVisit() ? 'Book My Visit' : 'Book My Viewing';
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    // Enter in an earlier step moves on rather than booking.
    if (current === 'start') return;
    if (current === '2') {
      if (!timeNext.disabled) timeNext.click();
      return;
    }
    if (current !== '3') return;

    const data = new FormData(form);
    const checked = {};
    for (const input of [nameInput, phoneInput, emailInput]) {
      const result = CHECKS[input.name](input.value);
      if (!result.ok) return fail(result.message, input);
      checked[input.name] = result.value;
    }
    const { name, phone, email } = checked;

    busy(true);
    const payload = {
      reason: isVisit() ? 'visit' : 'photos',
      kind,
      message: '',
      name,
      phone,
      email,
      website: data.get('website') || '',
      specialist: d.specialist,
      start: chosen ? chosen.iso : '',
      timeZone: zone,
      productHandle: d.productHandle,
      productTitle: d.productTitle,
      sku: d.sku,
      variantId: d.variantId,
      productUrl: d.productUrl,
      price: d.price,
      currency: d.currency,
      location: d.location,
      country: d.country,
      pageUrl: window.location.href,
    };

    try {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 15000);
      const response = await fetch(`${d.endpoint}?shop=${encodeURIComponent(d.shop)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit',
        signal: controller.signal,
      });
      window.clearTimeout(timer);
      const body = await response.json().catch(() => ({}));
      busy(false);
      if (response.ok && body.status === 'ok') return finish(body, { phone, email });
      if (response.status === 409) {
        // Someone took that time a moment ago: back to the times, freshly loaded.
        chosen = null;
        cache[kind].data = null;
        show('2');
        takenEl.textContent = body.message || 'That time was just taken. Please choose another.';
        takenEl.hidden = false;
        return;
      }
      return fail(body.message || `Something went wrong. Please try again, or call or text ${d.name} at ${d.phone}.`);
    } catch (_error) {
      busy(false);
      return fail(`We couldn’t book just now. Please try again, or call or text ${d.name} at ${d.phone}.`);
    }
  });

  // ------------------------------------------------------------ done

  const showDone = (saved, options) => {
    record = saved;
    setKind(saved.kind === 'visit' ? 'visit' : 'facetime');
    const start = saved.start ? new Date(saved.start) : null;
    q('[data-pdp-book-done-title]').textContent = saved.booked ? (isVisit() ? 'Your Visit Is Booked' : 'Your Viewing Is Booked') : 'Request Received';
    const when = q('[data-pdp-book-done-when]');
    when.textContent = start ? `${whenText(start)} (${zoneName})` : '';
    when.hidden = !start;
    const where = saved.place ? `our ${saved.city || city()} boutique, ${saved.place}` : `our ${saved.city || city()} boutique`;
    q('[data-pdp-book-done-text]').textContent = saved.booked
      ? isVisit()
        ? `${d.name} will meet you at ${where}. Your calendar invite is on its way to ${saved.email}.`
        : `${d.name} will FaceTime you at ${saved.phone}. Your calendar invite is on its way to ${saved.email}.`
      : start
        ? `${d.name} will confirm this time with you shortly, by text or email.`
        : `${d.name} will be in touch shortly to arrange a time for your ${isVisit() ? 'visit' : 'FaceTime viewing'}.`;
    calendars.hidden = !(saved.booked && start);
    if (!calendars.hidden) fillCalendarLinks();
    show('done', options);
  };

  const finish = (body, person) => {
    const saved = {
      kind,
      booked: Boolean(body.booked),
      start: body.start || null,
      end: body.end || null,
      icalUid: body.icalUid || null,
      place: body.place || (cache[kind].data && cache[kind].data.place) || null,
      city: body.city || (cache[kind].data && cache[kind].data.city) || d.city || null,
      phone: person.phone,
      email: person.email,
    };
    try {
      sessionStorage.setItem(STORE, JSON.stringify(saved));
    } catch (_error) {
      // private mode: the confirmation still shows now
    }
    showDone(saved);
  };

  // Add to Your Calendar: Google and Outlook open their own calendars in a new tab, filled in;
  // Apple gets a calendar file (opens in Calendar on iPhone and Mac), with the same UID as Google's
  // invite so it doesn't double up. A download alone didn't reach Gmail users on a computer (LDJ, 9 Oct).
  const eventTitle = () => `Luxe Du Jour ${isVisit() ? 'In-Person' : 'FaceTime'} Viewing · ${d.productTitle}`;
  const eventDetails = () =>
    isVisit() ? `${d.name} will meet you at our ${record.city || city()} boutique.\n${d.productUrl}` : `${d.name} will FaceTime you at ${record.phone}.\n${d.productUrl}`;
  const eventPlace = () => (isVisit() ? record.place || `Luxe Du Jour, ${record.city || city()}` : 'FaceTime');
  const stamp = (value) => new Date(value).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

  const fillCalendarLinks = () => {
    if (!record || !record.start || !record.end) return;
    const google = new URLSearchParams({
      action: 'TEMPLATE',
      text: eventTitle(),
      dates: `${stamp(record.start)}/${stamp(record.end)}`,
      details: eventDetails(),
      location: eventPlace(),
    });
    calendars.querySelector('[data-pdp-book-cal="google"]').href = `https://calendar.google.com/calendar/render?${google}`;
    const outlook = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject: eventTitle(),
      startdt: new Date(record.start).toISOString(),
      enddt: new Date(record.end).toISOString(),
      body: eventDetails(),
      location: eventPlace(),
    });
    calendars.querySelector('[data-pdp-book-cal="outlook"]').href = `https://outlook.live.com/calendar/0/deeplink/compose?${outlook}`;
  };

  calendars.querySelector('[data-pdp-book-cal="apple"]').addEventListener('click', () => {
    if (!record || !record.start || !record.end) return;
    const text = (value) => String(value).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/([,;])/g, '\\$1');
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Luxe Du Jour//FaceTime Viewings//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${record.icalUid || `${stamp(record.start)}-${d.variantId}@ldj.com`}`,
      `DTSTAMP:${stamp(Date.now())}`,
      `DTSTART:${stamp(record.start)}`,
      `DTEND:${stamp(record.end)}`,
      `SUMMARY:${text(eventTitle())}`,
      `DESCRIPTION:${text(eventDetails())}`,
      `LOCATION:${text(eventPlace())}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ];
    const url = URL.createObjectURL(new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' }));
    const link = Object.assign(document.createElement('a'), { href: url, download: isVisit() ? 'ldj-viewing.ics' : 'ldj-facetime-viewing.ics' });
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  });

  // ------------------------------------------------------------ opening

  // Start loading both kinds of times as soon as the shopper reaches for the row.
  const prefetch = () => {
    loadSlots(false, 'facetime');
    loadSlots(false, 'visit');
  };
  if (opener) {
    opener.addEventListener('pointerenter', prefetch, { once: true });
    opener.addEventListener('focus', prefetch, { once: true });
    opener.addEventListener('touchstart', prefetch, { once: true, passive: true });
    opener.addEventListener('click', prefetch);
  }

  // Every opening starts on the start screen, unless a viewing is already booked in this visit.
  if (dialog) {
    let wasOpen = false;
    new MutationObserver(() => {
      const isOpen = dialog.classList.contains('is-open');
      if (isOpen && !wasOpen) {
        if (current !== 'done') showStart({ focus: false });
        const title = current === 'start' ? startEl.querySelector('#PdpBookTitle-start') : steps[current].querySelector('.pdp-book__title');
        window.setTimeout(() => title?.focus({ preventScroll: true }), 60);
      }
      wasOpen = isOpen;
    }).observe(dialog, { attributes: true, attributeFilter: ['class'] });
  }

  // Booked earlier in this visit (and not over yet): the dialog opens on the confirmation.
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORE) || 'null');
    if (saved && (!saved.end || new Date(saved.end) > new Date())) showDone(saved, { focus: false });
  } catch (_error) {
    // nothing saved
  }
})();
